import * as THREE from 'three';

/**
 * A tiny original pixel game that plays on the astronaut's visor: a little
 * knight runs toward a glowing Mount Cinder, hopping fire pits and grabbing
 * coins. Drawn into a canvas every frame and used as a texture.
 */
const W = 160;
const H = 100;
const PX = 2; // canvas pixels per game pixel

// 1 = armour, 2 = plume, 3 = visor slit, 4 = cape, 5 = boots
const KNIGHT_A = [
  '...22...',
  '..222...',
  '.1111...',
  '.1331...',
  '.1111...',
  '411114..',
  '411114..',
  '.1111...',
  '.1..1...',
  '.5..5...',
];
const KNIGHT_B = [
  '...22...',
  '..222...',
  '.1111...',
  '.1331...',
  '.1111...',
  '411114..',
  '411114..',
  '.1111...',
  '..11....',
  '..55....',
];
const KNIGHT_JUMP = [
  '...22...',
  '..222...',
  '.1111...',
  '.1331...',
  '41111.4.',
  '411114..',
  '.1111...',
  '.1..1...',
  '5....5..',
  '........',
];
const COLORS: Record<string, string> = { '1': '#e9eef5', '2': '#ff7a2a', '3': '#1b1d26', '4': '#c8452f', '5': '#5b3a2a' };

// 5x7 bitmap glyphs for the ignition line, so it stays crisp at pixel scale.
const GLYPHS: Record<string, string[]> = {
  I: ['11111', '00100', '00100', '00100', '00100', '00100', '11111'],
  A: ['01110', '10001', '10001', '11111', '10001', '10001', '10001'],
  M: ['10001', '11011', '10101', '10101', '10001', '10001', '10001'],
  B: ['11110', '10001', '10001', '11110', '10001', '10001', '11110'],
  U: ['10001', '10001', '10001', '10001', '10001', '10001', '01110'],
  R: ['11110', '10001', '10001', '11110', '10100', '10010', '10001'],
  N: ['10001', '11001', '10101', '10101', '10011', '10001', '10001'],
  W: ['10001', '10001', '10001', '10101', '10101', '11011', '10001'],
  O: ['01110', '10001', '10001', '10001', '10001', '10001', '01110'],
  D: ['11110', '10001', '10001', '10001', '10001', '10001', '11110'],
  ' ': ['000', '000', '000', '000', '000', '000', '000'],
};

type Pit = { x: number; w: number };
type Coin = { x: number; y: number; got: boolean };

export class VisorGame {
  readonly canvas = document.createElement('canvas');
  readonly texture: THREE.CanvasTexture;
  private readonly g: CanvasRenderingContext2D;
  private scroll = 0;
  private y = 0;
  private vy = 0;
  private score = 0;
  private pits: Pit[] = [];
  private coins: Coin[] = [];
  private t = 0;
  private stars: [number, number, number][] = [];

  constructor() {
    this.canvas.width = W * PX;
    this.canvas.height = H * PX;
    this.g = this.canvas.getContext('2d')!;
    this.g.imageSmoothingEnabled = false;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.magFilter = THREE.NearestFilter;
    this.texture.minFilter = THREE.LinearFilter;
    for (let i = 0; i < 40; i++) this.stars.push([Math.random() * W, Math.random() * 40, Math.random()]);
    for (let i = 0; i < 6; i++) this.pits.push({ x: 70 + i * 55 + Math.random() * 20, w: 10 + Math.random() * 6 });
    for (let i = 0; i < 10; i++) this.coins.push({ x: 50 + i * 31, y: 52 - Math.random() * 16, got: false });
  }

  private px(x: number, y: number, w: number, h: number, c: string): void {
    this.g.fillStyle = c;
    this.g.fillRect(Math.round(x) * PX, Math.round(y) * PX, Math.max(1, Math.round(w)) * PX, Math.max(1, Math.round(h)) * PX);
  }

  /** `ignite` (0..1, scroll-driven) runs the eruption: Mount Cinder blows, ember floods the screen, the line burns in. */
  update(dt: number, ignite = 0): void {
    this.t += dt;
    const speed = 34 * (1 - Math.min(1, ignite * 1.6));
    this.scroll += speed * dt;
    const ground = 72;
    const heroX = 34;
    // Jump physics; auto-jump just before a pit edge.
    const onGround = this.y >= 0;
    const ahead = this.pits.find((p) => p.x - this.scroll > heroX - 2 && p.x - this.scroll < heroX + 9);
    if (onGround && ahead) this.vy = -78;
    this.vy += 210 * dt;
    this.y = Math.min(0, this.y + this.vy * dt);
    if (this.y >= 0) this.vy = 0;
    // Recycle pits and coins as they scroll past.
    for (const p of this.pits) if (p.x - this.scroll < -20) p.x += 6 * 55;
    for (const c of this.coins) {
      if (c.x - this.scroll < -10) { c.x += 10 * 31; c.got = false; c.y = 52 - Math.random() * 16; }
      const cx = c.x - this.scroll;
      if (!c.got && Math.abs(cx - (heroX + 3)) < 5 && Math.abs(c.y - (ground - 10 + this.y)) < 8) { c.got = true; this.score += 10; }
    }

    const g = this.g;
    // Sky: dusk purple to ember.
    const sky = g.createLinearGradient(0, 0, 0, H * PX);
    sky.addColorStop(0, '#1a1030');
    sky.addColorStop(0.55, '#5a2246');
    sky.addColorStop(1, '#ff8a3a');
    g.fillStyle = sky;
    g.fillRect(0, 0, W * PX, H * PX);
    for (const [sx, sy, tw] of this.stars) if (Math.sin(this.t * 3 + tw * 9) > -0.3) this.px((sx - this.scroll * 0.05 + W) % W, sy, 1, 1, '#fff1d6');
    // Mount Cinder in the distance, glowing top.
    // As the eruption starts, the mountain slides to centre stage.
    const pull = Math.min(1, ignite * 2.2);
    const mx = (110 - ((this.scroll * 0.1) % 220)) * (1 - pull * pull) + 42 * pull * pull;
    const rumble = ignite > 0.05 && ignite < 0.75 ? Math.sin(this.t * 60) * 1.2 * ignite : 0;
    g.save();
    g.translate(0, rumble * PX);
    for (let i = 0; i < 38; i++) this.px(mx + 38 - i, 62 - i * 0.9, i * 2, 1.2, '#2a1830');
    this.px(mx + 36, 27, 6, 2, '#ff7a2a');
    this.px(mx + 37, 25 + Math.sin(this.t * 6), 4, 2, '#ffd36e');
    // Rolling hills (parallax).
    for (let x = 0; x < W; x++) {
      const h = 8 + Math.sin((x + this.scroll * 0.4) * 0.07) * 4 + Math.sin((x + this.scroll * 0.4) * 0.19) * 2;
      this.px(x, ground - h, 1, h, '#3b2340');
    }
    // Ground with pits.
    for (let x = 0; x < W; x++) {
      const wx = x + this.scroll;
      const inPit = this.pits.some((p) => wx > p.x && wx < p.x + p.w);
      if (inPit) {
        const f = Math.sin(this.t * 14 + x) * 2;
        this.px(x, ground + 4 + f, 1, H - ground, '#ff5a1a');
        this.px(x, ground + 7 + f, 1, H - ground, '#ffd36e');
      } else {
        this.px(x, ground, 1, 2, '#7fb35a');
        this.px(x, ground + 2, 1, H - ground, (Math.floor(wx / 6) % 2) ? '#6b4430' : '#5b3a28');
      }
    }
    // Coins (spinning).
    for (const c of this.coins) {
      if (c.got) continue;
      const cx = c.x - this.scroll;
      const w = Math.abs(Math.cos(this.t * 5 + c.x)) * 4 + 1;
      this.px(cx + 2 - w / 2, c.y, w, 4, '#f2b733');
      this.px(cx + 2 - w / 4, c.y + 1, Math.max(1, w / 2), 1, '#fff2b0');
    }
    // The knight.
    const sprite = this.y < -1 ? KNIGHT_JUMP : Math.floor(this.t * 9) % 2 ? KNIGHT_A : KNIGHT_B;
    const ky = ground - sprite.length + this.y;
    sprite.forEach((row, r) => [...row].forEach((ch, c) => { if (ch !== '.') this.px(heroX + c, ky + r, 1, 1, COLORS[ch]); }));
    // HUD.
    g.font = `${7 * PX}px monospace`;
    g.fillStyle = '#fff1d6';
    g.fillText('BURNWOOD', 4 * PX, 9 * PX);
    g.fillStyle = '#ffd36e';
    g.fillText(String(this.score).padStart(5, '0'), (W - 32) * PX, 9 * PX);
    g.restore();
    if (ignite > 0.001) this.erupt(mx + 39, 26, ignite);
    // CRT scanlines and a soft vignette so it reads as a screen.
    g.fillStyle = 'rgba(0,0,0,0.18)';
    for (let y = 0; y < H * PX; y += 3) g.fillRect(0, y, W * PX, 1);
    const v = g.createRadialGradient(W * PX / 2, H * PX / 2, H * PX * 0.3, W * PX / 2, H * PX / 2, W * PX * 0.62);
    v.addColorStop(0, 'rgba(0,0,0,0)');
    v.addColorStop(1, 'rgba(0,0,0,0.65)');
    g.fillStyle = v;
    g.fillRect(0, 0, W * PX, H * PX);
    this.texture.needsUpdate = true;
  }

  private erupt(cx: number, cy: number, k: number): void {
    const g = this.g;
    // Lava fountain: deterministic particles so scrubbing back and forth stays stable.
    const n = Math.floor(90 * Math.min(1, k * 2));
    for (let i = 0; i < n; i++) {
      const life = (this.t * (0.6 + (i % 7) * 0.08) + i * 0.137) % 1;
      const vx = (Math.sin(i * 12.9898) * 0.5) * 70;
      const vy = -(55 + (Math.sin(i * 78.233) * 0.5 + 0.5) * 70);
      const x = cx + vx * life;
      const y = cy + vy * life + 120 * life * life;
      const c = life < 0.3 ? '#fff2b0' : life < 0.65 ? '#ffb347' : '#ff5a1a';
      this.px(x, y, 1 + (i % 3 === 0 ? 1 : 0), 1 + (i % 3 === 0 ? 1 : 0), c);
    }
    // Ember floods the screen from the crater.
    const flood = Math.max(0, Math.min(1, (k - 0.25) / 0.45));
    if (flood > 0) {
      const r = g.createRadialGradient(cx * PX, cy * PX, 2, cx * PX, cy * PX, W * PX * (0.2 + flood * 1.1));
      r.addColorStop(0, `rgba(255, 236, 170, ${0.95 * flood})`);
      r.addColorStop(0.35, `rgba(255, 138, 50, ${0.85 * flood})`);
      r.addColorStop(1, `rgba(120, 20, 10, ${0.75 * flood})`);
      g.fillStyle = r;
      g.fillRect(0, 0, W * PX, H * PX);
    }
    // The line burns in, letter by letter, with a flicker.
    const textK = Math.max(0, Math.min(1, (k - 0.55) / 0.35));
    if (textK > 0) {
      const lines = ['I AM', 'BURNWOOD'];
      const scale = 2;
      lines.forEach((line, li) => {
        const width = [...line].reduce((a, ch) => a + (GLYPHS[ch][0].length + 1) * scale, -scale);
        let x = Math.round((W - width) / 2);
        const y = 30 + li * 20;
        [...line].forEach((ch, ci) => {
          const shown = textK * (lines[0].length + lines[1].length) > ci + li * lines[0].length;
          const glyph = GLYPHS[ch];
          if (shown && Math.sin(this.t * 40 + ci * 3 + li) > -0.85) {
            glyph.forEach((row, ry) => [...row].forEach((b, rx) => {
              if (b === '1') {
                this.px(x + rx * scale + 1, y + ry * scale + 1, scale, scale, '#5a1206');
                this.px(x + rx * scale, y + ry * scale, scale, scale, '#fff6dc');
              }
            }));
          }
          x += (glyph[0].length + 1) * scale;
        });
      });
    }
  }
}
